import React from 'react';
import { Box, Button, Chip, Skeleton, Stack, Typography, useTheme } from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';
import MovieIcon from '@mui/icons-material/Movie';
import { useNavigate } from 'react-router-dom';

import { useLabs, useStudios } from '../../queries';
import { hexWithAlpha, themeGradient, LAB_CAPABILITIES } from './labCapabilities';
import type { LabCapability } from './labCapabilities';

const LAB_ICONS: Record<string, React.ReactElement> = {
  'image-lab': <ImageIcon />,
  'writer-lab': <DescriptionIcon />,
  'animation-lab': <MovieIcon />,
};

/**
 * "Your labs" — the Creative Cloud app-card pattern: each lab gets a card with
 * its icon, what it does, how many operations it exposes, and its studios as
 * deep-links, plus an explicit Open button.
 *
 * Visibility still follows the SMA-13 feature gate: a lab the backend omits
 * from `/api/v3/labs` is omitted here too, optimistically shown while the
 * catalog is still loading so cards don't flash out on first paint.
 */
export const LabShowcase: React.FC = () => {
  const { data: labsData } = useLabs();
  const labsLoaded = !!labsData;

  const visible = LAB_CAPABILITIES.filter(
    cap => !labsLoaded || labsData!.labs.some(l => l.id === cap.labId),
  );

  return (
    <Box>
      <Typography variant="h6" fontWeight={600} sx={{ mb: 1.5 }}>
        Your labs
      </Typography>
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="stretch">
        {visible.map(cap => (
          <LabCard
            key={cap.labId}
            capability={cap}
            name={labsData?.labs.find(l => l.id === cap.labId)?.name ?? cap.fallbackName}
            description={
              labsData?.labs.find(l => l.id === cap.labId)?.description ?? cap.fallbackDescription
            }
          />
        ))}
      </Stack>
    </Box>
  );
};

interface LabCardProps {
  capability: LabCapability;
  name: string;
  description: string;
}

const LabCard: React.FC<LabCardProps> = ({ capability, name, description }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const studios = useStudios(capability.labId);
  const accent = capability.accent(theme);

  return (
    <Box
      sx={{
        flex: '1 1 320px',
        minWidth: 290,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 3,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        transition: 'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 8,
          borderColor: hexWithAlpha(accent, 0.5),
        },
      }}
    >
      {/* Accent cap — the card's identity stripe, tinted per lab off the theme. */}
      <Box
        aria-hidden
        sx={{
          height: 6,
          background: themeGradient(theme, 90),
          opacity: theme.palette.mode === 'dark' ? 0.9 : 0.75,
        }}
      />

      <Stack spacing={2} sx={{ p: 2.5, flex: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box
            sx={{
              display: 'grid',
              placeItems: 'center',
              width: 44,
              height: 44,
              borderRadius: 2,
              flexShrink: 0,
              color: accent,
              bgcolor: hexWithAlpha(accent, 0.12),
            }}
          >
            {LAB_ICONS[capability.labId] ?? <ImageIcon />}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              {name}
            </Typography>
            <Typography variant="caption" sx={{ color: accent, fontWeight: 600 }}>
              {capability.operationCount} operations
            </Typography>
          </Box>
        </Stack>

        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          {description}
        </Typography>

        {/* Capability words — what the lab can do, at a glance. */}
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          {capability.highlights.map(h => (
            <Chip
              key={h}
              label={h}
              size="small"
              sx={{
                height: 22,
                fontSize: 11,
                bgcolor: hexWithAlpha(accent, 0.1),
                color: 'text.secondary',
              }}
            />
          ))}
        </Stack>

        {/* Studios as deep-links, straight from the backend catalog. */}
        <Box>
          <Typography variant="overline" color="text.secondary">
            Studios
          </Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
            {studios.isLoading ? (
              <Skeleton variant="rounded" width={180} height={26} />
            ) : (
              (studios.data?.studios ?? []).map(s => (
                <Chip
                  key={s.id}
                  label={s.name}
                  size="small"
                  variant="outlined"
                  clickable
                  onClick={() => navigate(`/labs/${capability.labId}/${s.id}`)}
                  sx={{ height: 26 }}
                />
              ))
            )}
          </Stack>
        </Box>

        <Button
          variant="contained"
          onClick={() => navigate(`/labs/${capability.labId}`)}
          sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 600, alignSelf: 'flex-start', px: 3 }}
          aria-label={`Open ${name}`}
        >
          Open
        </Button>
      </Stack>
    </Box>
  );
};
